import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '../Components/Layout/Layout';
import StyledSwitch from "../Components/Layout/StyledSwitch";
import { Card, CardContent } from "@/Components/ui/card";
import { Input } from "@/Components/ui/input";
import axiosInstance from '../utils/axiosConfig';
import QRCodeStyling from "qr-code-styling";
import { Plus, ImageDown, ExternalLink, RefreshCw } from "lucide-react";
import { useTranslation } from 'react-i18next';

const QRCodePage = () => {
    const { t } = useTranslation();
    const [url, setUrl] = useState('');
    const [options, setOptions] = useState({
        id: null,
        name: '',
        qrcode: null,
        ispublished: true,
        price: true,
        diets: true,
        allergens: true,
        translate: true,
        logo: '',
        backgroundcolor: '#ffffff',
        textcolor: '#000000',
        qrcodecolor: '#6a1a4c',
        font: 'Roboto',
        currency: '€' 
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [qrMounted, setQrMounted] = useState(false);
    const ref = useRef(null);
    const qrCodeRef = useRef(null);

    // Initialiseer QR code instance één keer
    if (!qrCodeRef.current) {
        qrCodeRef.current = new QRCodeStyling({
            width: 320,
            height: 320,
            dotsOptions: {
                color: options.qrcodecolor,
                type: "extra-rounded"
            },
            imageOptions: {
                crossOrigin: "anonymous",
                margin: 20
            }
        });
    }

    // Beschikbare valuta's
    const currencies = [
        { code: '€', name: 'Euro (€)', symbol: '€' },
        { code: '$', name: 'Dollar ($)', symbol: '$' },
        { code: '£', name: 'Pound (£)', symbol: '£' },
        { code: '¥', name: 'Yen (¥)', symbol: '¥' },
        { code: 'kr', name: 'Kroon (kr)', symbol: 'kr' },
        { code: 'CHF', name: 'Zwitserse Frank (CHF)', symbol: 'CHF' },
        { code: 'NOK', name: 'Noorse Kroon (NOK)', symbol: 'NOK' },
        { code: 'DKK', name: 'Deense Kroon (DKK)', symbol: 'DKK' }
    ];

    // Google Fonts opties
    const googleFonts = [
        { name: 'Roboto', category: 'Sans-serif', style: "'Roboto', sans-serif" },
        { name: 'Open Sans', category: 'Sans-serif', style: "'Open Sans', sans-serif" },
        { name: 'Lato', category: 'Sans-serif', style: "'Lato', sans-serif" },
        { name: 'Montserrat', category: 'Sans-serif', style: "'Montserrat', sans-serif" },
        { name: 'Poppins', category: 'Sans-serif', style: "'Poppins', sans-serif" },
        { name: 'Playfair Display', category: 'Serif', style: "'Playfair Display', serif" },
        { name: 'Merriweather', category: 'Serif', style: "'Merriweather', serif" },
        { name: 'PT Serif', category: 'Serif', style: "'PT Serif', serif" },
        { name: 'Ubuntu', category: 'Sans-serif', style: "'Ubuntu', sans-serif" },
        { name: 'Noto Sans', category: 'Sans-serif', style: "'Noto Sans', sans-serif" },
        { name: 'Source Sans Pro', category: 'Sans-serif', style: "'Source Sans Pro', sans-serif" },
        { name: 'Dancing Script', category: 'Handwriting', style: "'Dancing Script', cursive" },
        { name: 'Pacifico', category: 'Handwriting', style: "'Pacifico', cursive" },
        { name: 'Quicksand', category: 'Sans-serif', style: "'Quicksand', sans-serif" },
        { name: 'Oswald', category: 'Sans-serif', style: "'Oswald', sans-serif" }
    ];

    // Groepeer fonts per categorie
    const fontCategories = {
        'Sans-serif': googleFonts.filter(font => font.category === 'Sans-serif'),
        'Serif': googleFonts.filter(font => font.category === 'Serif'),
        'Handwriting': googleFonts.filter(font => font.category === 'Handwriting'),
    };

    // QR Code render functie
    const renderQRCode = useCallback((urlToRender, colorToUse) => {
        if (!ref.current || !urlToRender) return;

        try {
            // Clear bestaande content
            while (ref.current.firstChild) {
                ref.current.firstChild.remove();
            }

            // Update en render QR code
            qrCodeRef.current.update({
                data: urlToRender,
                width: 320,
                height: 320,
                dotsOptions: {
                    color: colorToUse || options.qrcodecolor,
                    type: "extra-rounded"
                }
            });

            qrCodeRef.current.append(ref.current);
            setQrMounted(true);
        } catch (error) {
            console.error('Error rendering QR code:', error);
            setQrMounted(false);
        }
    }, [options.qrcodecolor]);

    // Fetch instellingen - alleen één keer
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosInstance.get('/menu/GetQrSettings');
                const newOptions = {
                    ...response.data,
                    currency: response.data.currency || '€'
                };
                setOptions(newOptions);

                const newUrl = `${import.meta.env.VITE_APPURL}show/${response.data.qrcode}`;
                setUrl(newUrl);

                // Render QR code na het ophalen van data
                setTimeout(() => {
                    renderQRCode(newUrl, newOptions.qrcodecolor);
                }, 100);

            } catch (error) {
                console.error('Error fetching QR settings:', error);
                setError(t('qrcode.errorWhileLoadingData'));
            }
        };

        fetchSettings();
    }, []); // Lege dependency array - alleen bij mount

    // Load Google Fonts
    useEffect(() => {
        const loadGoogleFonts = () => {
            const link = document.createElement('link');
            link.href = 'https://fonts.googleapis.com/css2?family=' +
                googleFonts.map(font => font.name.replace(' ', '+')).join('&family=');
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        };

        loadGoogleFonts();
    }, []);

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (ref.current) {
                while (ref.current.firstChild) {
                    ref.current.firstChild.remove();
                }
            }
            setQrMounted(false);
        };
    }, []);

    // Handmatige refresh functie
    const handleRefreshQR = async () => {
        if (!isRefreshing && url) {
            setIsRefreshing(true);
            setTimeout(() => {
                renderQRCode(url, options.qrcodecolor);
                setIsRefreshing(false);
            }, 200);
        }
    };

    const onDownloadClick = () => {
        if (qrCodeRef.current) {
            qrCodeRef.current.download({
                extension: "png"
            });
        }
    };

    const onOpenClick = () => {
        window.open(url, '_blank');
    };

    const updateSettings = async (updatedOptions) => {
        setIsSaving(true);
        setError(null);

        try {
            await axiosInstance.post('/menu/SetQrSettings', {
                id: updatedOptions.id,
                name: updatedOptions.name,
                qrcode: updatedOptions.qrcode,
                ispublished: updatedOptions.ispublished,
                price: updatedOptions.price,
                diets: updatedOptions.diets,
                allergens: updatedOptions.allergens,
                translate: updatedOptions.translate,
                logo: updatedOptions.logo,
                backgroundcolor: updatedOptions.backgroundcolor,
                textcolor: updatedOptions.textcolor,
                qrcodecolor: updatedOptions.qrcodecolor,
                font: updatedOptions.font,
                currency: updatedOptions.currency
            });
        } catch (error) {
            console.error('Error updating QR settings:', error);
            setError(t('qrcode.errorWhileSavingData'));
            return false;
        } finally {
            setIsSaving(false);
        }
        return true;
    };

    const handleSwitchChange = async (checked, settingKey) => {
        const updatedOptions = {
            ...options,
            [settingKey]: checked
        };
        setOptions(updatedOptions);

        const success = await updateSettings(updatedOptions);
        if (!success) {
            setOptions(prevState => ({
                ...prevState,
                [settingKey]: !checked
            }));
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleStyleChange = async (name, value) => {
        const updatedOptions = {
            ...options,
            [name]: value
        };
        setOptions(updatedOptions);
        
        // Als QR code kleur verandert, update direct
        if (name === 'qrcodecolor' && url) {
            renderQRCode(url, value);
        }
        
        await updateSettings(updatedOptions);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('logo', file);

            try {
                setIsSaving(true);
                const response = await axiosInstance.post('/menu/UploadLogo', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.logoUrl) {
                    await handleStyleChange('logo', response.data.logoUrl);
                }
            } catch (error) {
                console.error('Error uploading logo:', error);
                setError(t('qrcode.errorWhileUploadingLogo'));
            } finally {
                setIsSaving(false);
            }
        }
    };

    const settings = [
        {
            id: 'publish-ispublished',
            title: t('qrcode.publish'),
            key: 'ispublished',
            description: t('qrcode.publishDescription')
        },
        {
            id: 'publish-price',
            title: t('qrcode.showPrices'),
            key: 'price',
            description: t('qrcode.showPricesDescription')
        },
        {
            id: 'publish-diets',
            title: t('qrcode.showDiets'),
            key: 'diets',
            description: t('qrcode.showDietsDescription') 
        },
        {
            id: 'publish-allergens',
            title: t('qrcode.showAllergens'),
            key: 'allergens',
            description: t('qrcode.showAllergensDescription')  
        },
        {
            id: 'publish-translate',
            title: t('qrcode.showTranslations'),
            key: 'translate',
            description: t('qrcode.showTranslationsDescription')   
        }
    ];

    return (
        <Layout>
            <div className="relative w-full max-w-[1100px] mx-auto px-4 md:px-6">
                <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-6">
                    {options.name}
                </h2>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                    {/* QR Code preview - Full width on mobile, 1/3 on desktop */}
                    <div className="w-full md:w-1/3">
                        <Card>
                            <CardContent className="p-4 md:p-6">
                                {options.qrcode ? (
                                    <>
                                        <div className="aspect-square w-full rounded-md flex items-center justify-center bg-white">
                                            <div
                                                ref={ref}
                                                className="max-w-full w-[380px] h-[320px] mx-auto"
                                                style={{ minHeight: '320px' }}
                                            />
                                        </div>
                                        <div className="mt-4 md:mt-8 flex justify-center gap-2">
                                            <button
                                                onClick={onDownloadClick}
                                                className="flex items-center gap-2 px-2 py-2 min-w-32 bg-ps-blue-dark text-white rounded-md hover:bg-ps-blue-darker"
                                            >
                                                <ImageDown className="w-5 h-5" />
                                                {t('qrcode.download')}
                                            </button>

                                            <button
                                                onClick={onOpenClick}
                                                className="flex items-center gap-2 px-2 py-2 min-w-32 bg-ps-blue-dark text-white rounded-md hover:bg-ps-blue-darker"
                                            >
                                                <ExternalLink className="w-5 h-5" />
                                                {t('qrcode.open')}
                                            </button>

                                            <button
                                                onClick={handleRefreshQR}
                                                disabled={isRefreshing}
                                                className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                                title={t('qrcode.reload')}
                                            >
                                                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="aspect-square w-full border rounded-md flex items-center justify-center bg-gray-50 text-gray-400">
                                        {t('qrcode.qrnotavailable')}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Settings - Full width on mobile, 2/3 on desktop */}
                    <div className="w-full md:flex-1">
                        <Card>
                            <CardContent className="p-4 md:p-6">
                                <div className="space-y-4 md:space-y-6">
                                    {settings.map(setting => (
                                        <div
                                            key={setting.id}
                                            className="flex flex-col sm:flex-row sm:items-center justify-between border-b last:border-b-0 pb-4 last:pb-0 gap-2 sm:gap-4"
                                        >
                                            <div>
                                                <h3 className="font-inter text-ps-blue-dark">
                                                    {setting.title}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {setting.description}
                                                </p>
                                            </div>
                                            <div className="self-start sm:self-center">
                                                <StyledSwitch
                                                    title={setting.title}
                                                    checked={options[setting.key]}
                                                    onCheckedChange={(checked) => handleSwitchChange(checked, setting.key)}
                                                    id={setting.id}
                                                    disabled={isSaving}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Styling options - Full width */}
                <div className="w-full mt-4 md:mt-6">
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <h3 className="text-lg font-inter text-ps-blue-dark mb-4">
                                {t('qrcode.stylingOptions')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('qrcode.logo')}
                                    </label>

                                    {/* Toon huidige logo als die bestaat */}
                                    {options.logo && (
                                        <div className="mb-3 p-3 bg-gray-50 rounded border">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={options.logo}
                                                    alt="Huidige logo"
                                                    className="w-12 h-12 object-contain bg-white rounded border"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                <div className="flex-1">
                                                    <p className="text-sm font-inter text-gray-700 mb-2">{t('qrcode.currentLogo')}:</p>
                                                    <img
                                                        src={`${import.meta.env.VITE_APIURL}menu/GetImage/${options.qrcode}`}
                                                        className="max-h-32 max-w-64 w-auto"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <Input
                                        type="file"
                                        name="logo"
                                        onChange={handleFileUpload}
                                        className="w-full p-2 border rounded bg-white"
                                        accept="image/*"
                                        disabled={isSaving}
                                    />
                                    {!options.logo && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('qrcode.noLogo')}
                                        </p>
                                    )}
                                </div>

                                {/* Currency Selection */}
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Valuta
                                    </label>
                                    <select
                                        name="currency"
                                        value={options.currency}
                                        onChange={(e) => handleStyleChange('currency', e.target.value)}
                                        className="w-full p-2 border rounded bg-white"
                                        disabled={isSaving}
                                    >
                                        {currencies.map(currency => (
                                            <option key={currency.code} value={currency.symbol}>
                                                {currency.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                    {t('qrcode.selectValuta')}
                                    </p>
                                </div>

                                {/* Colors */}
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('qrcode.colors')}
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500 min-w-[80px]">{t('qrcode.background')}:</span>
                                            <Input
                                                type="color"
                                                name="backgroundColor"
                                                value={options.backgroundcolor}
                                                onChange={(e) => handleStyleChange('backgroundcolor', e.target.value)}
                                                className="w-10 p-1 border rounded bg-white h-10"
                                                disabled={isSaving}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500 min-w-[80px]">{t('qrcode.text')}:</span>
                                            <Input
                                                type="color"
                                                name="textColor"
                                                value={options.textcolor}
                                                onChange={(e) => handleStyleChange('textcolor', e.target.value)}
                                                className="w-10 p-1 border rounded bg-white h-10"
                                                disabled={isSaving}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500 min-w-[80px]">{t('qrcode.qrcode')}:</span>
                                            <Input
                                                type="color"
                                                name="qrcodeColor"
                                                value={options.qrcodecolor}
                                                onChange={(e) => handleStyleChange('qrcodecolor', e.target.value)}
                                                className="w-10 p-1 border rounded bg-white h-10"
                                                disabled={isSaving}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Font selection */}
                                <div className="mb-2">
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('qrcode.font')}
                                    </label>
                                    <select
                                        name="font"
                                        value={options.font}
                                        onChange={(e) => handleStyleChange('font', e.target.value)}
                                        className="w-full p-2 border rounded bg-white"
                                        disabled={isSaving}
                                    >
                                        {Object.entries(fontCategories).map(([category, fonts]) => (
                                            <optgroup key={category} label={category}>
                                                {fonts.map(font => (
                                                    <option
                                                        key={font.name}
                                                        value={font.name}
                                                        style={{ fontFamily: font.style }}
                                                    >
                                                        {font.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};

export default QRCodePage;